<template>
  <div>
    <h1>{{ $t('project.documents.DocumentsHeader.documents') }}</h1>

    <p class="my-3 text-accent-lighten-2">
      {{ $t('project.documents.DocumentsHeader.storeAndExchangeDocuments') }}
      <AppInfoTooltip>
        <p>{{ $t('project.documents.DocumentsHeader.documentsPageInfo1') }}</p>
        <p>{{ $t('project.documents.DocumentsHeader.documentsPageInfo2') }}</p>
      </AppInfoTooltip>
    </p>

    <div class="noData">
      <v-img src="@/assets/img/documentIllustration.svg" width="325"/>
      <div class="noDataText">{{ $t('project.documents.Documents.noData') }}</div>

      <div v-if="displayAddMenu" class="d-flex column-gap-4">
        <v-menu open-on-hover :disabled="showSizeExceededTooltip">
          <template #activator="{ props: menuProps }">
            <AppTooltip :disabled="!showSizeExceededTooltip">
              <template #activator="{ props: tooltipProps }">
                <div v-bind="tooltipProps">
                  <app-button :disabled="showSizeExceededTooltip" v-bind="menuProps">
                    {{ $t("common.add") }}
                    <template #right-icon>
                      <app-icon-ds
                        icon="fa-chevron-down"
                        weight="far"
                        size="xs"
                      />
                    </template>
                  </app-button>
                </div>
              </template>
              {{ $t('project.documents.DocumentsHeader.sizeExceededTooltip') }}
            </AppTooltip>
          </template>

          <v-list>
            <v-list-item v-if="currentUserRights.canCreateFolders" @click="OPEN_DOCUMENTS_UPLOAD_FILES">
              <v-list-item-title>
                {{ $t("project.documents.DocumentsHeader.dropdownIcon.addFoldersOrFiles") }}
              </v-list-item-title>
            </v-list-item>
            <v-list-item v-else-if="currentUserRights.canUpload" @click="OPEN_DOCUMENTS_UPLOAD_FILES">
              <v-list-item-title>
                {{ $t("project.documents.DocumentsHeader.dropdownIcon.addFiles") }}
              </v-list-item-title>
            </v-list-item>
            <PolyOfficeListItemImport v-if="isImportFromPolyMenuDisplayed"
                                      @polyOfficeOpenDialog="polyOfficeOpenDialogHandler"/>
            <v-list-item v-if="(currentRoom.isPM || currentUserRights.canCreateFolders)"
                         @click="OPEN_DOCUMENTS_CREATE_NEW_FOLDER">
              <v-list-item-title>
                {{ $t("project.documents.DocumentsHeader.dropdownIcon.newFolder") }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>

        <app-button
          v-if="currentRoom.isPM"
          type="outlined"
          @click="existingTreeDialogIsOpen = true"
        >
          {{ $t("project.documents.DocumentsHeader.dropdownIcon.existingTree") }}
        </app-button>
      </div>

      <DocumentsExistingTreeDialog v-model:is-open="existingTreeDialogIsOpen" :mnemo="mnemo"/>

      <PolyOfficeDialog v-if="polyOfficeEnabled && dialogIsOpen" @callback="polyOfficeDialogCallback"/>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'

import AppInfoTooltip from '@/common/AppInfoTooltip.vue'
import AppTooltip from '@/common/AppTooltip.vue'
import { DocumentsImport } from '@/common/polyOffice/polyOffice.model'
import PolyOfficeDialog, { POLY_OFFICE_DIALOG_STATUS_SUCCESS } from '@/common/polyOffice/PolyOfficeDialog.vue'
import PolyOfficeListItemImport from '@/common/polyOffice/PolyOfficeListItemImport.vue'
import { toast } from '@/common/utils/toast'
import DocumentsExistingTreeDialog from '@/project/documents/dialogs/DocumentsExistingTreeDialog.vue'
import { DocumentsCreateNewFolderActionTypes } from '@/store/modules/documents-create-new-folder/action_types'
import { OPEN_DOCUMENTS_UPLOAD_FILES } from '@/store/modules/documents-upload-files/action_types'
import { GET_IMPORT_IFRAME } from '@/store/modules/poly-office/action_types'

export default {
  name: 'DocumentsEmptyState',
  components: {
    AppInfoTooltip,
    AppTooltip,
    DocumentsExistingTreeDialog,
    PolyOfficeDialog,
    PolyOfficeListItemImport,
  },
  props: {
    mnemo: {
      type: String,
      required: true,
    },
  },
  data () {
    return {
      existingTreeDialogIsOpen: false,
    }
  },
  computed: {
    ...mapState('documents', ['paginatedDocumentsCurrentFolderId']),
    ...mapGetters('room', ['isStorageFull']),
    ...mapState('room', ['currentRoom']),
    ...mapGetters('room', ['currentUserRights']),
    ...mapState('polyOffice', ['dialogIsOpen', 'dialogSuccess']),
    ...mapGetters('user', ['polyOfficeEnabled']),
    ...mapGetters('documents', ['isRootFolder']),
    showSizeExceededTooltip () {
      return this.isStorageFull
    },
    isImportFromPolyMenuDisplayed () {
      return this.polyOfficeEnabled &&
        (this.currentRoom.isPM || this.currentUserRights.canUpload)
    },
    displayAddMenu () {
      return (
        this.isRootFolder &&
        (
          this.currentRoom.isPM ||
          this.currentUserRights.canUpload ||
          this.currentUserRights.canCreateFolders
        )
      )
    },
  },
  methods: {
    ...mapActions('documentsUploadFiles', [OPEN_DOCUMENTS_UPLOAD_FILES]),
    ...mapActions('documentsCreateNewFolder', [DocumentsCreateNewFolderActionTypes.OPEN_DOCUMENTS_CREATE_NEW_FOLDER]),
    ...mapActions('polyOffice', [
      GET_IMPORT_IFRAME,
    ]),
    async polyOfficeOpenDialogHandler () {
      const payload = new DocumentsImport(
        this.mnemo,
        this.paginatedDocumentsCurrentFolderId,
      )
      await this.GET_IMPORT_IFRAME({
        payload,
        successMessage: this.$t('project.documents.DocumentsHeader.polyOffice.importSuccessful'),
      })
    },
    polyOfficeDialogCallback (data) {
      if (data.status === POLY_OFFICE_DIALOG_STATUS_SUCCESS) {
        this.refreshDocuments()
        toast.success(this.dialogSuccess)
      }
    },
  },
}
</script>

<style scoped>
.noData {
  display: flex;
  flex-direction: column;
  align-items: center;
  row-gap: 20px;
  text-align: center;
  margin-inline: auto;
  margin-top: 100px;
}
</style>
